import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Basemap } from "../types/basemaps";
import { DateFormat, TimeFormat } from "../types/dateTimeFormats";
import { Limits } from "../types/limits";
import { MeasurementSystem } from "../types/measurementSystems";
import { Subscription } from "../types/regions";

export type RegionsSettingsSlice = {
  loading: boolean;
  baseYear: number | null;
  centroid: Record<string, any> | null;
  createdAt: string | null;
  database: string | null;
  host: string | null;
  limits: Limits | null;
  metadata: unknown;
  modelType: string | null;
  modules: unknown[];
  mpoId: string | null;
  name: string | null;
  organizationId: number | null;
  port: number | null;
  id: number | null;
  srs: number | null;
  subscription: Subscription | null;
  updatedAt: string | null;
  zoom: number | null;
  shortDateFormat: DateFormat | null;
  shortDateFormatId: number | null;
  shortTimeFormat: TimeFormat | null;
  shortTimeFormatId: number | null;
  longDateFormat: DateFormat | null;
  longDateFormatId: number | null;
  longTimeFormat: TimeFormat | null;
  longTimeFormatId: number | null;
  measurementSystem: MeasurementSystem | null;
  measurementSystemId: number | null;
  basemap: Basemap | null;
  basemapId: number | null;
  residentialVacancyRate: unknown;
};

const initialState: RegionsSettingsSlice = {
  loading: false,
  baseYear: null,
  centroid: null,
  createdAt: null,
  database: null,
  host: null,
  limits: null,
  metadata: null,
  modelType: null,
  modules: [],
  mpoId: null,
  name: null,
  organizationId: null,
  port: null,
  id: null,
  srs: null,
  subscription: null,
  updatedAt: null,
  zoom: null,
  shortDateFormat: null,
  shortDateFormatId: null,
  shortTimeFormat: null,
  shortTimeFormatId: null,
  longDateFormat: null,
  longDateFormatId: null,
  longTimeFormat: null,
  longTimeFormatId: null,
  measurementSystem: null,
  measurementSystemId: null,
  basemap: null,
  basemapId: null,
  residentialVacancyRate: null
};

const regionSettingsSlice = createSlice({
  name: "regionSettings",
  initialState,
  reducers: {
    startGetRegionSettings(state) {
      state.loading = true;
      // state.getRegionSettingsError = false;
      // state.getRegionSettingsErrorMessage = "";
      // state.getRegionSettingsSuccess = false;
      // state.gettingRegionSettings = false;
    },
    getRegionSettingsSuccess(state, action: PayloadAction<any>) {
      state.loading = false;
      state.baseYear = action.payload.baseYear;
      state.centroid = action.payload.centroid;
      state.createdAt = action.payload.createdAt;
      state.database = action.payload.database;
      state.host = action.payload.host;
      state.id = action.payload.id;
      state.limits = action.payload.limits;
      state.metadata = action.payload.metadata;
      state.modelType = action.payload.modelType;
      state.modules = action.payload.modules;
      state.mpoId = action.payload.mpoId;
      state.name = action.payload.name;
      state.organizationId = action.payload.organizationId;
      state.port = action.payload.port;
      state.srs = action.payload.srs;
      state.subscription = action.payload.subscription;
      state.updatedAt = action.payload.updatedAt;
      state.zoom = action.payload.zoom;
      state.shortDateFormat = action.payload.shortDateFormat;
      state.shortDateFormatId = action.payload.shortDateFormatId;
      state.shortTimeFormat = action.payload.shortTimeFormat;
      state.shortTimeFormatId = action.payload.shortTimeFormatId;
      state.longDateFormat = action.payload.longDateFormat;
      state.longDateFormatId = action.payload.longDateFormatId;
      state.longTimeFormat = action.payload.longTimeFormat;
      state.longTimeFormatId = action.payload.longTimeFormatId;
      state.measurementSystem = action.payload.measurementSystem;
      state.measurementSystemId = action.payload.measurementSystemId;
      state.basemap = action.payload.basemap;
      state.basemapId = action.payload.basemapId;
      state.residentialVacancyRate = action.payload.residentialVacancyRate;
    },
    getRegionSettingsFailure(state) {
      state.loading = false;
      state.baseYear = null;
      state.centroid = null;
      state.createdAt = null;
      state.database = null;
      // state.getRegionSettingsError = false;
      // state.getRegionSettingsErrorMessage = "";
      // state.getRegionSettingsSuccess = false;
      // state.gettingRegionSettings = false;
      state.host = null;
      state.id = null;
      state.limits = null;
      state.metadata = null;
      state.modelType = null;
      state.modules = [];
      state.mpoId = null;
      state.name = null;
      state.organizationId = null;
      state.port = null;
      state.srs = null;
      state.subscription = null;
      state.updatedAt = null;
      state.zoom = null;
      state.shortDateFormat = null;
      state.shortDateFormatId = null;
      state.shortTimeFormat = null;
      state.shortTimeFormatId = null;
      state.longDateFormat = null;
      state.longDateFormatId = null;
      state.longTimeFormat = null;
      state.longTimeFormatId = null;
      state.measurementSystem = null;
      state.measurementSystemId = null;
      state.basemap = null;
      state.basemapId = null;
      state.residentialVacancyRate = null;
    },
    //NOTE: This action is never used
    setRegionSettings(state, action: PayloadAction<any>) {
      state.baseYear = action.payload.baseYear;
      state.centroid = action.payload.centroid;
      state.createdAt = action.payload.createdAt;
      state.database = action.payload.database;
      state.host = action.payload.host;
      state.id = action.payload.id;
      state.limits = action.payload.limits;
      state.metadata = action.payload.metadata;
      state.modelType = action.payload.modelType;
      state.modules = action.payload.modules;
      state.mpoId = action.payload.mpoId;
      state.name = action.payload.name;
      state.organizationId = action.payload.organizationId;
      state.port = action.payload.port;
      state.srs = action.payload.srs;
      state.subscription = action.payload.subscription;
      state.updatedAt = action.payload.updatedAt;
      state.zoom = action.payload.zoom;
      state.shortDateFormat = action.payload.shortDateFormat;
      state.shortDateFormatId = action.payload.shortDateFormatId;
      state.shortTimeFormat = action.payload.shortTimeFormat;
      state.shortTimeFormatId = action.payload.shortTimeFormatId;
      state.longDateFormat = action.payload.longDateFormat;
      state.longDateFormatId = action.payload.longDateFormatId;
      state.longTimeFormat = action.payload.longTimeFormat;
      state.longTimeFormatId = action.payload.longTimeFormatId;
      state.measurementSystem = action.payload.measurementSystem;
      state.measurementSystemId = action.payload.measurementSystemId;
      state.basemap = action.payload.basemap;
      state.basemapId = action.payload.basemapId;
      state.residentialVacancyRate = action.payload.residentialVacancyRate;
    }
  }
});

export const {
  startGetRegionSettings,
  getRegionSettingsSuccess,
  getRegionSettingsFailure
} = regionSettingsSlice.actions;

export default regionSettingsSlice.reducer;
